import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Plane, MapPin, Sun, Moon, Sunset, ShoppingBag, Wallet, Phone, Camera, BedDouble } from 'lucide-react';

const TravelPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Travel', color: t.accent };

  const renderItinerary = () => {
    const dayPlan = page.dayPlan || [];
    const timeIcons = { 'Morning': Sun, 'Afternoon': Sunset, 'Evening': Moon };
    return (
      <div className="flex-1 overflow-hidden">
        {dayPlan.length > 0 ? (
          <div className="space-y-2">
            {dayPlan.map((block, bi) => {
              const TimeIcon = timeIcons[block.timeBlock] || Sun;
              return (
                <div key={bi} className="rounded-xl p-2.5" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <TimeIcon className="w-3 h-3" style={{ color: t.accent }} />
                    <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 700, color: t.accent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{block.timeBlock}</span>
                  </div>
                  <p style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 600, color: t.text }}>{block.activity}</p>
                  {block.location && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="w-2 h-2" style={{ color: t.subtext }} />
                      <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>{block.location}</span>
                    </div>
                  )}
                  {block.notes && <p style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', fontStyle: 'italic', color: t.subtext, marginTop: '2px' }}>{block.notes}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          /* Fallback: render fields as time blocks */
          <div className="space-y-2">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontWeight: 500, color: t.text }}>{f}</span>
                <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPacking = () => {
    const categories = page.packingCategories || [];
    return (
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingBag className="w-3 h-3" style={{ color: t.accent }} />
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontWeight: 600, color: t.text }}>Packing Checklist</span>
        </div>
        {categories.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat, ci) => (
              <div key={ci} className="rounded-lg p-2" style={{ backgroundColor: t.fieldBg || t.bg }}>
                <h4 className="text-[8px] font-bold uppercase tracking-wider mb-1" style={{ color: t.accent }}>{cat.category}</h4>
                <div className="space-y-0.5">
                  {(cat.items || []).map((item, ii) => (
                    <div key={ii} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
                      <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.text }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded border" style={{ borderColor: t.accent + '40' }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderBudget = () => {
    const budgetCats = page.budgetCategories || [];
    return (
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-3 h-3" style={{ color: t.accent }} />
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontWeight: 600, color: t.text }}>Budget Tracker</span>
        </div>
        {budgetCats.length > 0 ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2 pb-1" style={{ borderBottom: `1px solid ${t.accent}20` }}>
              <span className="flex-1 text-[8px] font-bold uppercase" style={{ color: t.subtext }}>Category</span>
              <span className="w-16 text-[8px] font-bold uppercase text-center" style={{ color: t.subtext }}>Estimated</span>
              <span className="w-16 text-[8px] font-bold uppercase text-center" style={{ color: t.subtext }}>Actual</span>
            </div>
            {budgetCats.map((cat, ci) => (
              <div key={ci} className="flex items-center gap-2 py-0.5">
                <span className="flex-1" style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text }}>{cat.category}</span>
                <span className="w-16 text-center" style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.accent, fontWeight: 600 }}>{cat.estimated || ''}</span>
                <div className="w-16 h-4 rounded" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1 mt-1" style={{ borderTop: `2px solid ${t.accent}30` }}>
              <span className="flex-1 font-bold" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>Total</span>
              <div className="w-16 h-5 rounded" style={{ backgroundColor: t.accentLight || t.accent + '10', border: `1px solid ${t.accent}30` }} />
              <div className="w-16 h-5 rounded" style={{ backgroundColor: t.accentLight || t.accent + '10', border: `1px solid ${t.accent}30` }} />
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, minWidth: '4rem' }}>{f}</span>
                <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMemories = () => (
    <div className="flex-1 flex flex-col">
      <div className="rounded-xl flex flex-col items-center justify-center mb-3" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25`, minHeight: '5rem' }}>
        <Camera className="w-5 h-5 mb-1" style={{ color: t.accent + '40' }} strokeWidth={1.5} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.accent + '60' }}>Place photo here</span>
      </div>
      <div className="space-y-1.5">
        {(page.fields || []).map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, minWidth: '3rem' }}>{f}:</span>
            <div className="flex-1 h-4 rounded" style={{ borderBottom: `1px dashed ${t.accent}25` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500, minWidth: '5rem' }}>{f}</span>
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'travel-itinerary': renderItinerary,
    'travel-packing': renderPacking,
    'travel-budget': renderBudget,
    'travel-memories': renderMemories,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Plane className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">
        {page.content && !contentMap[page.pageType] && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
        {render()}
      </div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default TravelPage;
